import axios from "axios";
import useAppStore from "../stores/appStore";
import { isDeletedUserError } from "../utils/authErrors";
import {
  performSessionLogout,
  shouldSkipAuthInterceptor,
} from "../utils/sessionLogout";
import {
  isAxiosCanceledError,
  isAxiosNetworkError,
  markAppOffline,
  notifySuccessfulApiResponse,
} from "../utils/internetConnection";
import { getAuthToken, hasAuthToken } from "../utils/authToken";

// Backend uses non-auth statuses (e.g. 407) for assessment validation errors.
const shouldSkipSessionLogoutForAssessmentAdmin = (
  url: string | undefined,
  error: unknown
): boolean => {
  const requestUrl = String(url ?? "");
  const isAssessmentAdmin =
    requestUrl.includes("/assessment/add") ||
    requestUrl.includes("/assessment/actual_list") ||
    requestUrl === "/assessment" ||
    requestUrl.endsWith("/assessment");
  if (!isAssessmentAdmin) return false;

  const status =
    error && typeof error === "object" && "response" in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;

  if (status === 407 || status === 402 || status === 409) return true;
  return status !== undefined && status !== 401;
};

export const baseFetch = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

baseFetch.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const { focus, domain } = useAppStore.getState();
    const noAuthHeader = config.headers?.["No-Auth"];
    const skipAuth =
      noAuthHeader === true ||
      noAuthHeader === "true" ||
      noAuthHeader === "1";

    if (token && !skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
      let headerFocus = focus;
      let headerDomain = domain;
      if (!headerFocus || !headerDomain) {
        try {
          const params = new URLSearchParams(window.location.search);
          if (!headerFocus) headerFocus = params.get("focus") || "";
          if (!headerDomain) headerDomain = params.get("domain") || "";
        } catch {
          // ignore invalid location
        }
      }
      if (headerFocus) {
        config.headers.focus = `${headerFocus}`;
      }
      if (headerDomain) {
        config.headers.domain = `${headerDomain}`;
      }
    } else {
      delete config.headers.Authorization;
    }
    if (noAuthHeader !== undefined) {
      delete config.headers["No-Auth"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

baseFetch.interceptors.response.use(
  (response) => {
    notifySuccessfulApiResponse();
    return response;
  },
  (error) => {
    if (isAxiosCanceledError(error)) {
      return Promise.reject(error);
    }

    if (isAxiosNetworkError(error)) {
      const requestUrl = String(error.config?.url ?? "");
      const skipOfflineUi =
        requestUrl.includes("/assessments/history") ||
        requestUrl.includes("/assessments/team") ||
        requestUrl.includes("/assessments/send") ||
        requestUrl.includes("/assessment_manager_dashboard") ||
        requestUrl.includes("/assessment/actual_list") ||
        requestUrl.includes("/assessment/add") ||
        requestUrl === "/assessment" ||
        requestUrl.endsWith("/assessment") ||
        requestUrl.includes("/integration/search");
      const {
        isAssessmentOpen,
        isPreassessmentOpen,
        isUserMenuModalOpen,
      } = useAppStore.getState();
      if (
        !skipOfflineUi &&
        !isAssessmentOpen &&
        !isPreassessmentOpen &&
        !isUserMenuModalOpen
      ) {
        markAppOffline();
      }
    }

    if (
      isDeletedUserError(error) &&
      hasAuthToken() &&
      navigator.onLine &&
      !shouldSkipAuthInterceptor(error.config?.url) &&
      !shouldSkipSessionLogoutForAssessmentAdmin(error.config?.url, error)
    ) {
      console.warn("Session is invalid. Logging out...");
      void performSessionLogout();
    }
    return Promise.reject(error);
  }
);

export const tokenFetch = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

tokenFetch.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const noAuthHeader = config.headers?.["No-Auth"];
    const skipAuth =
      noAuthHeader === true ||
      noAuthHeader === "true" ||
      noAuthHeader === "1";

    if (token && !skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    if (noAuthHeader !== undefined) {
      delete config.headers["No-Auth"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

tokenFetch.interceptors.response.use(
  (response) => {
    notifySuccessfulApiResponse();
    return response;
  },
  (error) => Promise.reject(error)
);
